// imports
import debug from 'debug'

// lib files
import { ArgumentError } from "@/lib/errors"
import { ContentServiceCollection, PlatformType } from "@/lib/types/api/content-service";
import { CS_CACHE_TIME, CONTENT_SERVICE_HOST } from '@/lib/constants';
import { ShowRowContent } from "@/lib/types/api/show-data";
import cachedFetch from '@/lib/helpers/cached-fetch';
import stationServices from '@/lib/station-services/client';
import uuidv4 from "@/lib/helpers/uuid";

const logger = debug('pbs:content-services:direct')

const directId = uuidv4().split('-')[0]

function log(message: string) {
  logger(`${directId} | ${message}`)
}

const CONTENT_SERVICE_BASE_URL = `${CONTENT_SERVICE_HOST}/v3/`
export const CONTENT_SERVICE_PBSORG_URL = `${CONTENT_SERVICE_BASE_URL}pbsorg`
export const CONTENT_SERVICE_SVP_URL = `${CONTENT_SERVICE_BASE_URL}svp`
const username = 'cristi'
const password = 'test'

const oneHourInSeconds = 3600

const credentials = btoa(`${username}:${password}`)
const authHeader = `Basic ${credentials}`

export const headers = {
  Authorization: authHeader,
  'X-PBS-PlatformVersion': '2.0.0'
}

export const URLForPlatform = (platform: 'svp' | 'pbsorg') => `${CONTENT_SERVICE_BASE_URL}${platform}`

export const getOptions = { method: 'GET', headers }

export async function cachedCSFetch(url: string, ttl: number = CS_CACHE_TIME) {
  return await cachedFetch(url, ttl, getOptions)
}

const buildParams = (stationId?: string, countryId?: string, start: number = 0, limit: number = 50): URLSearchParams => {
  const params = new URLSearchParams()
  if(stationId) { params.append('station_id', stationId) }
  if(countryId) { params.append('country_id', countryId) }
  params.append('start', start.toString())
  params.append('limit', limit.toString())
  return params
}

// deals with CS paginated responses
export async function loopThroughPaginatedCSData(route: string, ttl: number = CS_CACHE_TIME, includeRecommendationID: boolean = false) {
  let responseData = []
  let lastResult = null;

  do {
    const json = await cachedCSFetch(route, ttl);

    if (!json.collections) {
      return null
    }

    if(json.collections.length === 0) {
      return responseData
    }

    lastResult = json
    responseData.push(json.collections[0].content)
    responseData = responseData.flat()
    route = lastResult.meta.links.next
  } while (route !== null)

  if (responseData.length > 0) {
    if (includeRecommendationID) {
      const recommendationID = lastResult.collections[0].display_metadata.recommendation_id;
      // add recommendationID to each object in the array
      responseData = responseData.map((data: ShowRowContent) => {
        return {
          ...data,
          recommendation_id: recommendationID
        }
      })
    }
    return responseData;
  } else {
    return null;
  }
}

export async function getMessageData(platform: PlatformType) {
  const route = `${CONTENT_SERVICE_BASE_URL}${platform}/messages/`
  const response = await cachedCSFetch(route)
  return response
}

export async function getPopularShows(platform: PlatformType, stationId?: string, countryId?: string) {
  if(!stationId && !countryId) {
    throw new ArgumentError('Must provide either stationId, countryId or both.')
  }

  const route = `${URLForPlatform(platform)}/collections/popular-shows/`
  const params = buildParams(stationId, countryId, 0, 24);

  const url = `${route}?${params}`

  const json = await cachedCSFetch(url)
  return json.collections[0].content as ShowRowContent[]
}

export async function getEpisodesForSeason(platform: PlatformType, slug: string, cid: string, stationId?: string, countryId?: string) {
  if(!stationId && !countryId) {
    throw new ArgumentError('Must provide either stationId, countryId or both.')
  }

  const seasonParams = buildParams(stationId, countryId)
  const route = `${URLForPlatform(platform)}/screens/shows/${slug}/seasons/${cid}/episodes/?${seasonParams}`
  return await loopThroughPaginatedCSData(route)
}

// @TODO DRY out this function - it really only changes the /episodes/ to /assets/
export async function getExtrasForSeason(platform: PlatformType, slug: string, cid: string, stationId?: string, countryId?: string) {
  if(!stationId && !countryId) {
    throw new ArgumentError('Must provide either stationId, countryId or both.')
  }

  const seasonParams = buildParams(stationId, countryId)
  const route = `${URLForPlatform(platform)}/screens/shows/${slug}/seasons/${cid}/assets/?${seasonParams}`
  return await loopThroughPaginatedCSData(route)
}

// The default CS response for a show only includes 12 specials - but some shows, e.g. American Masters, have many more than that
export async function getSpecialsForShow(platform: PlatformType, slug: string, stationId?: string, countryId?: string) {
  if(!stationId && !countryId) {
    throw new ArgumentError('Must provide either stationId, countryId or both.')
  }

  const specialsParams = buildParams(stationId, countryId)
  const route = `${URLForPlatform(platform)}/screens/shows/${slug}/specials/?${specialsParams}`
  return await loopThroughPaginatedCSData(route)
}

// Show level extras have a different route
export async function getShowLevelExtras(platform: PlatformType, slug: string, stationId?: string, countryId?: string) {
  if(!stationId && !countryId) {
    throw new ArgumentError('Must provide either stationId, countryId or both.')
  }

  const params = buildParams(stationId, countryId);
  const route = `${URLForPlatform(platform)}/screens/shows/${slug}/assets/?${params}`
  return await loopThroughPaginatedCSData(route)
}

export async function getLivestreamSchedule(platform: PlatformType, stationId: string) {
  const params = new URLSearchParams()
  params.append('station_id', stationId)
  params.append('item_count', '20')
  params.append('all_livestreams', 'true')

  const route = `${URLForPlatform(platform)}/schedules/livestreams/?${params}`

  const json = await cachedFetch(route, oneHourInSeconds, getOptions)
  return json.collections[0]
}

export async function getStationDataFromStationsList(stationId: string) {
  return await stationServices.getStationDataByStationId(stationId)
}

export async function getStationData(platform: PlatformType, stationId: string) {
  const params = new URLSearchParams()
  params.append('station_id', stationId)

  const route = `${URLForPlatform(platform)}/screens/stations/${stationId}/?${params}`

  const json = await cachedCSFetch(route)
  return json.resource
}

export async function getRelatedAssetForVideo(platform: PlatformType, slug: string, stationId?: string, countryId?: string) {
  if(!stationId) {
    throw new ArgumentError('Must provide a stationId')
  }

  const relatedAssetParams = buildParams(stationId, countryId)
  const route = `${URLForPlatform(platform)}/screens/video-assets/${slug}/related/?${relatedAssetParams}`;
  const json = await cachedCSFetch(route)
  return json.resource
}

export async function getStationLinks(platform: PlatformType, stationId: string) {
  if(!stationId) {
    throw new ArgumentError('Must provide a stationId')
  }

  const stationLinksParams = new URLSearchParams()
  stationLinksParams.append('station_id', stationId)

  const route = `${URLForPlatform(platform)}/collections/station-links/?${stationLinksParams}`;
  log(`Fetching Station Links for Station ID: ${stationId}`)

  const json = await cachedCSFetch(route)
  return json.collections[0].content
}

export async function getExploreTopicData(
  platform: PlatformType,
  cs_api_slug: string,
  cs_genre_slug?: string | null,
  stationId?: string,
  countryId?: string,
  ) {
  const exploreTopicParams = buildParams(stationId, countryId)

  if (cs_genre_slug) { exploreTopicParams.append('genre_slug', cs_genre_slug) }

  const route = `${URLForPlatform(platform)}/screens/${cs_api_slug}/?${exploreTopicParams.toString()}`;

  const json = await cachedCSFetch(route)
  return json.collections
}

export async function getShowPageData(slug: string, platform: PlatformType, stationId?: string, countryId?: string) {
  const showPageParams = buildParams(stationId, countryId);

  const route = `${URLForPlatform(platform)}/screens/shows/${slug}/?${showPageParams}`;

  const json = await cachedCSFetch(route)
  return json
}

export async function getVideoPageData(slug: string, platform: PlatformType, stationId?: string, countryId?: string) {
  const videoPageParams = buildParams(stationId, countryId);

  const route = `${URLForPlatform(platform)}/screens/video-assets/${slug}/?${videoPageParams}`;

  const json = await cachedCSFetch(route)
  return json
}

export async function getFranchisePageData(platform: PlatformType, slug: string, stationId?: string, countryId?: string) {
  const franchisePageParams = new URLSearchParams()
  if (stationId) { franchisePageParams.append('station_id', stationId) }
  if (countryId) { franchisePageParams.append('country_id', countryId) }

  const route = `${URLForPlatform(platform)}/screens/franchises/${slug}/?${franchisePageParams}`;

  const json = await cachedCSFetch(route)
  return json
}

export async function getFranchiseShowsData(platform: PlatformType, slug: string, countryId?:string, stationId?: string): Promise<ShowRowContent[] | null> {
  const params = new URLSearchParams()
  params.append('franchise_slug', slug)
  params.append('start', '0')
  params.append('limit', '50')

  if(countryId) {
    params.append('country_id', countryId)
  }

  if(stationId) {
    params.append('station_id', stationId)
  }

  let route = `${URLForPlatform(platform)}/collections/franchise-shows/?${params}`;

  let responseData = [];
  let lastResult;

  do {
    try {
      const json = await cachedCSFetch(route)
      lastResult = json
      responseData.push(json.collections[0].content)
      responseData = responseData.flat()
      route = lastResult.meta.links.next
    } catch (err) {
      console.error(`Something is wrong ${err}`)
      break;
    }
  } while (route !== null)

  if (responseData.length > 0) {
    return responseData;
  } else {
    return null;
  }
}

async function getContentServiceEndpointAsCollections(
  route: string,
  platform: PlatformType,
  stationId?: string,
  countryId?: string) {
  if(!stationId && !countryId) {
    throw new ArgumentError('Must provide either stationId, countryId or both.')
  }

  const csCallParams = buildParams(stationId, countryId)

  const routeToFetch = `${CONTENT_SERVICE_BASE_URL}${platform}/${route}/?${csCallParams.toString()}`

  const json = await cachedCSFetch(routeToFetch)
  return json.collections as Array<ContentServiceCollection>;
}

export async function getHomePageData(platform: PlatformType, stationId?: string, countryId?: string) {
  const route = `screens/home`
  return await getContentServiceEndpointAsCollections(route, platform, stationId, countryId);
}

export async function getTopNewsData(stationId?: string, countryId?: string) {
  const route = `collections/top-news`
  return await getContentServiceEndpointAsCollections(route, 'pbsorg', stationId, countryId);
}

export async function getPassportLearnMoreData(platform: 'pbsorg' | 'svp', stationId?: string, countryId?: string) {
  const route = `screens/passport-learn-more`
  return await getContentServiceEndpointAsCollections(route, platform, stationId, countryId);
}

export async function getPassportLandingData(platform: 'pbsorg' | 'svp', stationId?: string, countryId?: string) {
  const route = `screens/passport-landing`
  return await getContentServiceEndpointAsCollections(route, platform, stationId, countryId);
}

export async function getMyStationData(stationId?: string, countryId?: string) {
  const route = `screens/my-station`
  return await getContentServiceEndpointAsCollections(route, 'pbsorg', stationId, countryId);
}

export async function getCollectionForShow (
  showSlug: string,
  collectionSlug: string,
  stationId?: string,
  countryId?: string,
  platform: 'pbsorg' | 'svp' = 'pbsorg'
) {
  const showCollectionParams = buildParams(stationId, countryId, 0, 50);
  const route = `${CONTENT_SERVICE_BASE_URL}${platform}/screens/shows/${showSlug}/collections/${collectionSlug}/?${showCollectionParams.toString()}`

  const json = await cachedCSFetch(route)

  if (!json || !json.collections) {
    return null
  }
  // we need to get all of the collection videos,
  const videos = await loopThroughPaginatedCSData(route)

  // then splice them in
  json.collections[0].content = videos;

  return json
}

export async function getUpNextSuggestions(platform: PlatformType , stationId?: string, countryId?: string) {
  const route = 'collections/up-next-suggestions'
  return await getContentServiceEndpointAsCollections(route, platform, stationId, countryId);
}

export async function getFullWidthShowPosterHighlightPassportImage(platform: PlatformType, stationId?: string) {
  const route = 'collections/full-width-show-poster-highlight-passport'
  // RWEB-9226 users outside the us won't have a stationId, and the getContentServiceEndpointAsCollections requires
  // either a stationId or countryId. So we'll just set the countryId _always_ to US - for this collection it doesn't change anything.
  return await getContentServiceEndpointAsCollections(route, platform, stationId, 'US');
}

